/**
 * Composants tactiles partagés pour les fiches d'évaluation interactives
 * (Plateau, Circulation, Salle).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { FICHE_BY_CODE } from '../../data/teacher/evalInference';

/**
 * Mini-lien « Fiche cours » à placer à côté d'une compétence REMC.
 * Affiché uniquement si une fiche cours est câblée pour ce code.
 */
export function FicheCoursLink({ code, accent = '#1d4ed8' }) {
  const info = FICHE_BY_CODE[code];
  if (!info) return null;
  return (
    <Link
      to={`${info.hub}/${info.slug}`}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border transition-colors hover:bg-muted flex-shrink-0"
      style={{ borderColor: `${accent}55`, color: accent }}
      data-testid={`fiche-cours-link-${code}`}
      title="Voir la fiche cours à donner"
    >
      <FileText className="w-3 h-3" />
      Fiche
    </Link>
  );
}

export function TouchCheckbox({ checked, onChange, label, color = '#ea580c', testid, extra }) {
  return (
    <label
      className={`flex items-center gap-2.5 p-2.5 rounded-xl border-2 cursor-pointer transition-all select-none ${
        checked ? 'text-foreground' : 'bg-background border-border hover:border-foreground/30'
      }`}
      style={checked ? { background: `${color}1a`, borderColor: color } : undefined}
      data-testid={testid}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        style={checked ? { background: color, borderColor: color } : undefined}
      />
      <span className="text-sm font-semibold leading-tight flex-1">{label}</span>
      {extra}
    </label>
  );
}

export function TouchOuiNon({ value, onChange, label, testid, extra }) {
  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded-xl border border-border bg-background/40" data-testid={testid}>
      <span className="text-sm font-semibold flex-1">{label}</span>
      {extra}
      <div className="flex gap-1">
        {['oui', 'non'].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(value === v ? '' : v)}
            data-testid={`${testid}-${v}`}
            className={`px-3 py-1 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors ${
              value === v
                ? v === 'oui'
                  ? 'bg-[#16a34a] text-white'
                  : 'bg-[#cf2031] text-white'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TouchTriple({ value, onChange, options, label, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded-xl border border-border bg-background/40" data-testid={testid}>
      <span className="text-sm font-semibold flex-1">{label}</span>
      <div className="flex gap-1 flex-wrap justify-end">
        {options.map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(value === opt.v ? '' : opt.v)}
            data-testid={`${testid}-${opt.v}`}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-colors ${
              value === opt.v ? 'text-white' : 'bg-muted text-muted-foreground'
            }`}
            style={value === opt.v ? { background: opt.color } : undefined}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function EvalSection({ n, icon: Icon, label, duree, color, children, testid }) {
  const [open, setOpen] = useState(true);
  return (
    <section
      className="rounded-2xl bg-card border border-border/60 overflow-hidden"
      style={{ borderLeft: `5px solid ${color}` }}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 sm:p-5 text-left hover:bg-muted/30 transition-colors no-print"
        aria-expanded={open}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
          style={{ background: color }}
        >
          {n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon className="w-4 h-4" style={{ color }} />
            <h3 className="text-sm sm:text-base font-black tracking-tight">{label}</h3>
            {duree && (
              <span
                className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: `${color}1a`, color }}
              >
                {duree}
              </span>
            )}
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      <div className={`p-4 sm:p-5 pt-0 border-t border-border/40 bg-background/40 ${open ? '' : 'hidden print:block'}`}>
        {children}
      </div>
    </section>
  );
}

export const APPREHENSION_OPTS = [
  { v: 'faible', label: 'Faible', color: '#16a34a' },
  { v: 'moyen', label: 'Moyen', color: '#f59e0b' },
  { v: 'important', label: 'Important', color: '#cf2031' },
];
